/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { cancelEvent } from '../../utils/UNSAFE_visUtils';
import type { GanttCurrentRowLabel, GanttRowData, GanttTaskData } from '../gantt.types';
import type { getRowDataLayout } from '../utils/dataLayoutUtils';
import { getAboveRowLabelNavigable, getBelowRowLabelNavigable } from '../utils/navigationUtils';

type UseGanttRowLabelNavigationOptions<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = {
  rowDataLayout: ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;
  currentRowLabel: GanttCurrentRowLabel<K1>['currentRowLabel'];
  onCurrentRowLabelChange: GanttCurrentRowLabel<K1>['onCurrentRowLabelChange'];
  isDisabled?: boolean;
};

/**
 * Hook that handles row labels keyboard navigation.
 */
const useGanttRowLabelNavigation = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  rowDataLayout,
  currentRowLabel,
  onCurrentRowLabelChange,
  isDisabled = false
}: UseGanttRowLabelNavigationOptions<K1, K2, D1, D2>) => {
  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'ArrowUp': {
        const aboveRowId =
          currentRowLabel && getAboveRowLabelNavigable(currentRowLabel.id, rowDataLayout);
        if (aboveRowId !== undefined) {
          onCurrentRowLabelChange({ id: aboveRowId, isFocused: true });
        }
        cancelEvent(event);
        break;
      }
      case 'ArrowDown': {
        const belowRowId =
          currentRowLabel && getBelowRowLabelNavigable(currentRowLabel.id, rowDataLayout);
        if (belowRowId !== undefined) {
          onCurrentRowLabelChange({ id: belowRowId, isFocused: true });
        }
        cancelEvent(event);
        break;
      }
    }
  };

  return isDisabled ? {} : { onKeyDown };
};

export { useGanttRowLabelNavigation };
